import React, { useState, useEffect, useMemo } from "react";
import { apiFetch } from "../../../../apiFetch";
import "./ApproveBetaAccess.css";

const ApproveBetaAccess = () => {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(false);

    const [betaUsers, setBetaUsers] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState("");

    const [showSuggestions, setShowSuggestions] = useState(false);



    // =========================
    // FETCH BETA USERS
    // =========================
    const fetchBetaUsers = async () => {
        try {
            setListLoading(true);
            setListError("");

            const res = await apiFetch("/api/admin/beta-users");


            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData?.message || `Failed to load beta users (${res.status})`);
            }

            const data = await res.json();
            setBetaUsers(data?.users || []);
        } catch (err) {
            console.error("❌ Fetch beta users error:", err);
            setBetaUsers([]);
            setListError(err.message || "Failed to load beta users");
        } finally {
            setListLoading(false);
        }
    };


    useEffect(() => {
        fetchBetaUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // =========================
    // AUTOSUGGEST FILTER
    // =========================
    const filteredSuggestions = useMemo(() => {
        const value = email.toLowerCase().trim();
        if (!value) return [];

        return betaUsers
            .filter((u) => u.email?.toLowerCase().includes(value))
            .slice(0, 8); // ✅ limit suggestions so it doesn't get huge
    }, [email, betaUsers]);

    // =========================
    // APPROVE ACCESS
    // =========================
    const handleApprove = async () => {
        if (!email.trim()) {
            setStatus("❌ Please enter a valid email.");
            return;
        }

        const cleanEmail = email.toLowerCase().trim();

        try {
            setLoading(true);
            setStatus("⏳ Approving beta access...");

            const res = await apiFetch("/api/admin/approve-beta-access", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: cleanEmail }),
            });


            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data?.message || "Approval failed");
            }


            setStatus(`✅ Beta access approved for ${cleanEmail}`);
            setEmail("");
            setShowSuggestions(false);
            fetchBetaUsers();
        } catch (err) {
            console.error("❌ Approve error:", err);

            setStatus(`❌ ${err.message || "Approval failed"}`);

        } finally {
            setLoading(false);
        }
    };

    // =========================
    // REVOKE ACCESS
    // =========================
    const handleRevoke = async () => {
        if (!email.trim()) {
            setStatus("❌ Please enter a valid email.");
            return;
        }

        const cleanEmail = email.toLowerCase().trim();

        try {
            setLoading(true);
            setStatus("⏳ Revoking beta access...");

            const res = await apiFetch("/api/admin/revoke-beta-access", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: cleanEmail }),
            });


            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data?.message || "Revoke failed");
            }


            setStatus(`❌ Beta access revoked for ${cleanEmail}`);
            setEmail("");
            setShowSuggestions(false);
            fetchBetaUsers();
        } catch (err) {
            console.error("❌ Revoke error:", err);

            setStatus(`❌ ${err.message || "Revoke failed"}`);

        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="approve-beta-access">
            <h2>🚀 Beta Access Control</h2>

            <p>
                Manually <strong>approve or revoke</strong> Early Bird Beta access.
                <br />
                <strong>Admin only.</strong>
            </p>

            {/* ✅ INPUT + AUTOCOMPLETE */}
            <div className="email-autocomplete">
                <input
                    type="email"
                    placeholder="User email"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    disabled={loading}
                />

                {showSuggestions && filteredSuggestions.length > 0 && (
                    <div className="suggestions-box">
                        {filteredSuggestions.map((u) => (
                            <div
                                key={u.id}
                                className="suggestion-item"
                                onClick={() => {
                                    setEmail(u.email);
                                    setShowSuggestions(false);
                                }}
                            >
                                <div className="suggestion-email">{u.email}</div>
                                <div className="suggestion-meta">
                                    {u.role} • {u.betaAccess ? "✔ Beta" : "✖ No Beta"}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <button onClick={handleApprove} disabled={loading}>
                {loading ? "Processing..." : "Approve Access"}
            </button>

            <button onClick={handleRevoke} disabled={loading} className="revoke-btn">
                Revoke Access
            </button>

            {status && <p className="status-message">{status}</p>}

            {/* =========================
          BETA USERS LIST
      ========================= */}
            <div style={{ marginTop: "30px", textAlign: "left" }}>
                <div className="beta-users-header">
                    <h3>🧪 Beta Users</h3>

                    <button
                        onClick={fetchBetaUsers}
                        disabled={listLoading}
                        className="refresh-btn"
                    >
                        {listLoading ? "Refreshing..." : "Refresh"}
                    </button>
                </div>

                {listError && <p className="list-error">❌ {listError}</p>}

                {listLoading ? (
                    <p>Loading users...</p>
                ) : betaUsers.length === 0 ? (
                    <p>No users found.</p>
                ) : (
                    <div className="beta-users-list-container">
                        <ul>
                            {betaUsers.map((user) => (
                                <li key={user.id}>
                                    <strong>{user.email}</strong> — {user.role}
                                    <span
                                        style={{
                                            marginLeft: "10px",
                                            color: user.betaAccess ? "lime" : "red",
                                        }}
                                    >
                                        {user.betaAccess ? "✔ Beta" : "✖ No Beta"}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ApproveBetaAccess;









